import { cn } from "@/lib/cn";
import { AgentAvatar } from "@/workshop/components/canvas/agent-avatar";

/**
 * Workshop: Sidebar task list item — checkbox, title, and mini agent avatar.
 */

interface SidebarTaskItemProps {
  title: string;
  checked?: boolean;
  agentLetter?: string;
  agentColor?: string;
  onToggle?: () => void;
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function SidebarTaskItem({
  title,
  checked = false,
  agentLetter = "D",
  agentColor,
  onToggle,
  onClick,
  className,
  "data-testid": testId,
}: SidebarTaskItemProps) {
  return (
    <div
      className={cn("flex h-7 w-full items-center gap-2 px-4 transition-colors hover:bg-glass-stroke-subtle", className)}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex h-3 w-3 shrink-0 items-center justify-center rounded-[2px] border",
          checked
            ? "border-accent bg-accent"
            : "border-border bg-transparent",
        )}
        aria-label={checked ? "Mark incomplete" : "Mark complete"}
        data-testid={testId ? `${testId}-checkbox` : undefined}
      >
        {checked && (
          <svg
            className="h-2 w-2 text-white"
            viewBox="0 0 8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1.5 4L3.5 6L6.5 2" />
          </svg>
        )}
      </button>

      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex-1 truncate text-left text-xs text-secondary",
          checked && "line-through opacity-60",
        )}
        data-testid={testId ? `${testId}-title` : undefined}
      >
        {title}
      </button>

      <AgentAvatar
        letter={agentLetter}
        size="sm"
        hideStatus
        className={agentColor}
        data-testid={testId ? `${testId}-agent` : undefined}
      />
    </div>
  );
}

export type { SidebarTaskItemProps };
